import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { orderApi, settingsApi } from '../../services/api';
import { STATUS_COLORS } from '../../adminTheme';
import ReceiptGenerator from '../../components/common/ReceiptGenerator';

const C = {
  card:'#ffffff', border:'#f0e4d8', border2:'#f5ede4',
  text1:'#1a0f00', text2:'#3d2010', text3:'#6b4c35', text4:'#a07858',
  accent:'#c96428', accentL:'#fff4ee', accentB:'#f0d0b0',
  surface:'#fdfaf7', surface2:'#f9f3ee',
};

const STATUS_FLOW = ['pending','confirmed','preparing','ready','delivered','cancelled'];

const OrderModal = ({ order, onClose, onUpdate, gst={enabled:false,rate:0,label:'GST'}, deliveryCharge=0, slug='' }) => {
  const [updating,    setUpdating]    = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const sc = STATUS_COLORS[order.status] || STATUS_COLORS.pending;

  const handleStatus = async (status) => {
    setUpdating(true);
    try { await orderApi.updateStatus(order.id, status); toast.success(`Order marked as ${status}`); onUpdate(); onClose(); }
    catch { toast.error('Update failed'); }
    finally { setUpdating(false); }
  };

  const itemsTotal    = (order.items||[]).reduce((s,i)=>s+parseFloat(i.subtotal||i.price*i.quantity),0);
  const discountAmt   = parseFloat(order.discount_amount || 0);
  const couponCode    = order.coupon_code || null;
  const afterDiscount = Math.max(itemsTotal - discountAmt, 0);
  const tax           = gst.enabled && gst.rate > 0 ? (afterDiscount * gst.rate) / 100 : 0;
  const subtotal      = afterDiscount;
  const deliveryFee   = order.order_type === 'delivery' ? deliveryCharge : 0;
  const total         = subtotal + tax + deliveryFee;

  return (
    <>
      <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
          style={{ position:'absolute', inset:0, background:'rgba(26,15,0,0.5)', backdropFilter:'blur(4px)' }} onClick={onClose}/>
        <motion.div initial={{opacity:0,y:20,scale:0.97}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,scale:0.97}}
          style={{ position:'relative', background:C.card, borderRadius:22, width:'100%', maxWidth:480, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,0.2)', border:`1px solid ${C.border}` }}>

          {/* Header */}
          <div style={{ padding:'22px 24px 18px', borderBottom:`1px solid ${C.border2}`, background:`linear-gradient(135deg,${C.accentL},#fff)` }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
              <div>
                <p style={{ fontFamily:'"Playfair Display",serif', fontSize:20, fontWeight:800, color:C.text1, margin:0 }}>Order #{order.order_number || order.id}</p>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 12px', borderRadius:99, fontSize:11, fontWeight:700, textTransform:'capitalize', background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, marginTop:6 }}>
                  {order.status}
                </span>
              </div>
              <button onClick={onClose}
                style={{ width:34, height:34, borderRadius:10, border:`1px solid ${C.border}`, background:C.card, cursor:'pointer', color:C.text3, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
            </div>
          </div>

          <div style={{ padding:24, display:'flex', flexDirection:'column', gap:20 }}>

            {/* Customer info */}
            <div style={{ background:C.surface, borderRadius:14, overflow:'hidden', border:`1px solid ${C.border2}` }}>
              {[['Customer',order.customer_name],['Phone',order.phone],['Email',order.email||'—'],['Type',(order.order_type||'dine-in').replace('-',' ')],
                ...(order.table_number?[['🪑 Table',order.table_number]]:[]),
                ...(order.address?[['Delivery Address',order.address]]:[]),
                ['Time',new Date(order.created_at).toLocaleString('en-IN')]].map(([l,v],i,arr) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'11px 16px', borderBottom:i<arr.length-1?`1px solid ${C.border2}`:'none' }}>
                  <span style={{ color:C.text4, fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:0.3 }}>{l}</span>
                  <span style={{ color:C.text1, fontWeight:600, fontSize:13, textTransform:'capitalize' }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Items */}
            <div>
              <p style={{ color:C.text3, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Items Ordered</p>
              <div style={{ background:C.surface, borderRadius:14, overflow:'hidden', border:`1px solid ${C.border2}` }}>
                {(order.items||[]).map((item,i,arr) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom:i<arr.length-1?`1px solid ${C.border2}`:'none' }}>
                    <div>
                      <p style={{ color:C.text1, fontWeight:600, fontSize:13, margin:0, textTransform:'capitalize' }}>{item.item_name}</p>
                      <p style={{ color:C.text4, fontSize:11, margin:'2px 0 0' }}>₹{parseFloat(item.price).toFixed(0)} × {item.quantity}</p>
                    </div>
                    <span style={{ color:C.accent, fontWeight:800, fontSize:14 }}>₹{parseFloat(item.subtotal).toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total breakdown */}
            <div style={{ background:C.accentL, borderRadius:14, border:`1px solid ${C.accentB}`, padding:'16px 18px' }}>
              {discountAmt > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, padding:'8px 12px', borderRadius:10, background:'#dcfce7', border:'1px solid #a5d6a7' }}>
                  <span style={{ color:'#16a34a', fontSize:13, fontWeight:700 }}>🎟️ {couponCode || 'Discount'}</span>
                  <span style={{ color:'#16a34a', fontWeight:700, fontSize:13 }}>-₹{discountAmt.toFixed(0)}</span>
                </div>
              )}
              {gst.enabled && gst.rate > 0 && (
                <>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ color:C.text3, fontSize:13 }}>Subtotal</span>
                    <span style={{ color:C.text2, fontWeight:600, fontSize:13 }}>₹{subtotal.toFixed(0)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12, paddingBottom:12, borderBottom:`1px dashed ${C.accentB}` }}>
                    <span style={{ color:C.text3, fontSize:13 }}>{gst.label} ({gst.rate}%)</span>
                    <span style={{ color:C.accent, fontWeight:600, fontSize:13 }}>+₹{tax.toFixed(0)}</span>
                  </div>
                </>
              )}
              {order.order_type === 'delivery' && (
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12, paddingBottom:12, borderBottom:`1px dashed ${C.accentB}` }}>
                  <span style={{ color:C.accent, fontSize:13, fontWeight:600 }}>🛵 Delivery Charge</span>
                  <span style={{ color:C.accent, fontWeight:700, fontSize:13 }}>{deliveryFee > 0 ? `+₹${deliveryFee.toFixed(0)}` : 'FREE'}</span>
                </div>
              )}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontFamily:'"Playfair Display",serif', fontSize:17, fontWeight:800, color:C.text1 }}>Total</span>
                <span style={{ fontSize:22, fontWeight:800, color:C.accent }}>₹{total.toFixed(0)}</span>
              </div>
            </div>

            {/* Download receipt */}
            <button onClick={() => setShowReceipt(true)}
              style={{ width:'100%', padding:'13px 0', borderRadius:12, border:'none', background:`linear-gradient(135deg,#c96428,#9a4a1e)`, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 4px 16px rgba(201,100,40,0.3)' }}>
              📥 Download Receipt
            </button>

            {/* Status actions */}
            {order.status !== 'delivered' && order.status !== 'cancelled' && (
              <div>
                <p style={{ color:C.text3, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Update Status</p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  {STATUS_FLOW.filter(s => s !== order.status).map(s => {
                    const sc2 = STATUS_COLORS[s] || STATUS_COLORS.pending;
                    return (
                      <button key={s} disabled={updating} onClick={() => handleStatus(s)}
                        style={{ padding:'10px 14px', borderRadius:11, border:`1.5px solid ${sc2.border}`, background:sc2.bg, color:sc2.color, fontSize:12, fontWeight:700, fontFamily:'inherit', cursor:'pointer', textTransform:'capitalize', opacity:updating?0.6:1, transition:'all 0.15s' }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
      {showReceipt && <ReceiptGenerator order={order} slug={slug} onClose={() => setShowReceipt(false)}/>}
    </>
  );
};

const AdminOrders = () => {
  const { slug } = useParams();
  const [orders,       setOrders]       = useState([]);
  const [gst,          setGst]          = useState({enabled:false,rate:0,label:'GST'});
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [exportMonth,  setExportMonth]  = useState(new Date().toISOString().slice(0,7));
  const [exporting,    setExporting]    = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [filter,       setFilter]       = useState('');
  const [selected,     setSelected]     = useState(null);
  const [selectedFull,  setSelectedFull]  = useState(null);
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  const [deleteOrderNumber, setDeleteOrderNumber] = useState(null);
  const [deleting,      setDeleting]      = useState(false);

  const handleDeleteOrder = async () => {
    if (!deleteOrderId) return;
    setDeleting(true);
    try {
      await orderApi.delete(deleteOrderId);
      toast.success('Order deleted!');
      setDeleteOrderId(null);
      setDeleteOrderNumber(null);
      fetchOrders();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  useEffect(() => {
    settingsApi.get().then(r => {
      const d = r.data.data||{};
      setGst({enabled:d.gst_enabled==='true',rate:parseFloat(d.gst_rate)||0,label:d.gst_label||'GST'});
      setDeliveryCharge(parseFloat(d.delivery_charge) || 0);
    }).catch(()=>{});
  },[]);

  const fetchOrders = async () => {
    setLoading(true);
    try { const res = await orderApi.getAll(filter?{status:filter}:{}); setOrders(res.data.data||[]); }
    catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line
  useEffect(()=>{ fetchOrders(); },[filter]);

  const openDetail = async (order) => {
    setSelected(order);
    try { const res = await orderApi.getOne(order.id); setSelectedFull(res.data.data); }
    catch { setSelectedFull(order); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('cafe_admin_token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://cafe-app-u6w5.onrender.com/api';
      const res = await fetch(apiUrl+'/admin/orders/export/pdf?month='+exportMonth, { headers:{Authorization:'Bearer '+token} });
      if (!res.ok) throw new Error('Failed');
      const html = await res.text();
      const newTab = window.open('','_blank');
      newTab.document.write(html);
      newTab.document.close();
      const btn = newTab.document.createElement('button');
      btn.innerText='⬇️ Download / Print'; btn.className='no-print';
      btn.style.cssText='position:fixed;top:16px;right:16px;padding:10px 20px;background:#c96428;color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer;z-index:9999;box-shadow:0 4px 16px rgba(201,100,40,0.4)';
      btn.onclick=()=>newTab.print(); newTab.document.body.appendChild(btn);
      const closeBtn = newTab.document.createElement('button');
      closeBtn.innerText='✕ Close'; closeBtn.className='no-print';
      closeBtn.style.cssText='position:fixed;top:16px;right:200px;padding:10px 20px;background:rgba(255,255,255,0.95);color:#1a0f00;border:1.5px solid #e8d5c4;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;z-index:9999;box-shadow:0 2px 12px rgba(0,0,0,0.1);backdrop-filter:blur(4px);';
      closeBtn.onclick=()=>newTab.close(); newTab.document.body.appendChild(closeBtn);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const activeOrders = orders.filter(o => o.status !== 'cancelled');
  const totalRevenue = activeOrders.reduce((s,o)=>s+parseFloat(o.total_amount),0);
  const totalTax     = gst.enabled&&gst.rate>0 ? activeOrders.reduce((s,o)=>{ const t=parseFloat(o.total_amount); return s+(t-t/(1+gst.rate/100)); },0) : 0;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:24}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontFamily:'"Playfair Display",serif',fontSize:26,fontWeight:800,color:C.text1,margin:0}}>Orders</h1>
          <p style={{color:C.text4,fontSize:13,marginTop:4}}>{orders.length} order{orders.length!==1?'s':''} {filter?`· ${filter}`:''}</p>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <input type="month" value={exportMonth} onChange={e=>setExportMonth(e.target.value)}
            style={{padding:'9px 14px',borderRadius:11,border:`1.5px solid ${C.border}`,fontSize:13,color:C.text1,fontFamily:'inherit',outline:'none',background:C.surface}}/>
          <button onClick={handleExport} disabled={exporting}
            style={{padding:'10px 20px',borderRadius:11,background:exporting?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)',color:'#fff',border:'none',fontSize:13,fontWeight:700,cursor:exporting?'not-allowed':'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:6,boxShadow:exporting?'none':'0 4px 14px rgba(201,100,40,0.3)'}}>
            📄 {exporting?'Generating...':'Export Details'}
          </button>
          <button onClick={fetchOrders}
            style={{padding:'10px 16px',borderRadius:11,border:`1.5px solid ${C.border}`,background:C.card,color:C.text3,fontSize:13,fontFamily:'inherit',cursor:'pointer',fontWeight:600}}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {orders.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:14}}>
          {[
            {label:'Total Orders',value:activeOrders.length,icon:'📦',color:'#2563eb',colorL:'#dbeafe'},
            {label:'Total Revenue',value:`₹${totalRevenue.toFixed(0)}`,icon:'💰',color:'#16a34a',colorL:'#dcfce7'},
            ...(gst.enabled&&gst.rate>0?[
              {label:`${gst.label} Collected`,value:`₹${totalTax.toFixed(0)}`,icon:'🧾',color:'#7c3aed',colorL:'#ede9fe'},
              {label:'Net Revenue',value:`₹${(totalRevenue-totalTax).toFixed(0)}`,icon:'📊',color:C.accent,colorL:C.accentL},
            ]:[]),
          ].map(s=>(
            <div key={s.label} style={{background:C.card,borderRadius:14,padding:'16px 18px',border:`1px solid ${C.border}`,boxShadow:'0 2px 8px rgba(0,0,0,0.04)',display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:40,height:40,borderRadius:11,background:s.colorL,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>{s.icon}</div>
              <div>
                <p style={{color:C.text4,fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:0.4,margin:0}}>{s.label}</p>
                <p style={{color:s.color,fontSize:18,fontWeight:800,margin:'2px 0 0'}}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter tabs */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {['',...STATUS_FLOW].map(s=>{
          const active=filter===s;
          const sc=s?STATUS_COLORS[s]:null;
          return (
            <button key={s} onClick={()=>setFilter(s)}
              style={{padding:'8px 18px',borderRadius:99,fontSize:12,fontWeight:700,fontFamily:'inherit',cursor:'pointer',textTransform:'capitalize',transition:'all 0.15s',
                background:active?(sc?sc.bg:C.accentL):'#fff',
                color:active?(sc?sc.color:C.accent):C.text3,
                border:`1.5px solid ${active?(sc?sc.border:C.accentB):C.border}`,
                boxShadow:active?'0 2px 8px rgba(0,0,0,0.1)':'none',
              }}>
              {s||'All'}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{background:C.card,borderRadius:18,border:`1px solid ${C.border}`,overflow:'hidden',boxShadow:'0 2px 12px rgba(0,0,0,0.05)'}}>
        {loading ? (
          <div style={{padding:24,display:'flex',flexDirection:'column',gap:10}}>
            {Array(5).fill(0).map((_,i)=><div key={i} style={{height:52,borderRadius:12,background:'#f5ede4',animation:'pulse 1.5s infinite'}}/>)}
          </div>
        ) : orders.length===0 ? (
          <div style={{textAlign:'center',padding:'64px 24px',color:C.text4}}>
            <div style={{fontSize:44,marginBottom:12}}>📦</div>
            <p style={{fontSize:15,fontWeight:600,color:C.text3,margin:0}}>No orders {filter?`with status "${filter}"`:''}</p>
            <p style={{fontSize:13,marginTop:6}}>Orders will appear here once customers place them</p>
          </div>
        ) : (
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{background:'#fdf8f4'}}>
                  {['#','Customer','Type','Items','Amount','Status','Date',''].map(h=>(
                    <th key={h} style={{textAlign:'left',padding:'13px 16px',color:C.text3,fontWeight:700,fontSize:11,textTransform:'uppercase',letterSpacing:0.5,whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(order=>{
                  const sc=STATUS_COLORS[order.status]||STATUS_COLORS.pending;
                  const t=parseFloat(order.total_amount);
                  return (
                    <tr key={order.id} onClick={()=>openDetail(order)}
                      style={{borderBottom:`1px solid ${C.border2}`,cursor:'pointer',transition:'background 0.12s'}}
                      onMouseEnter={e=>e.currentTarget.style.background='#fff8f3'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{padding:'14px 16px',color:C.accent,fontFamily:'monospace',fontWeight:700,fontSize:13}}>#{order.order_number || order.id}</td>
                      <td style={{padding:'14px 16px',color:C.text1,fontWeight:600,textTransform:'capitalize'}}>{order.customer_name}</td>
                      <td style={{padding:'14px 16px'}}>
                        <span style={{background:C.surface2,color:C.text3,borderRadius:99,padding:'3px 10px',fontSize:11,fontWeight:600,textTransform:'capitalize'}}>{order.order_type}</span>
                        {order.table_number && (
                          <span style={{marginLeft:6,background:'#f0fdf4',color:'#15803d',borderRadius:99,padding:'3px 8px',fontSize:11,fontWeight:700,border:'1px solid #a5d6a7'}}>
                            🪑 {order.table_number}
                          </span>
                        )}
                      </td>
                      <td style={{padding:'14px 16px'}}>
                        <span style={{background:C.surface2,color:C.text3,borderRadius:99,padding:'3px 10px',fontSize:11,fontWeight:600}}>{order.item_count} items</span>
                      </td>
                      <td style={{padding:'14px 16px'}}>
                        <span style={{color:C.accent,fontWeight:800,fontSize:14}}>₹{parseFloat(order.total_amount).toFixed(0)}</span>
                      </td>
                      <td style={{padding:'14px 16px'}}>
                        <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 12px',borderRadius:99,fontSize:11,fontWeight:700,textTransform:'capitalize',background:sc.bg,color:sc.color,border:`1px solid ${sc.border}`}}>{order.status}</span>
                      </td>
                      <td style={{padding:'14px 16px',color:C.text4,fontSize:12,whiteSpace:'nowrap'}}>{new Date(order.created_at).toLocaleDateString('en-IN')}</td>
                      <td style={{padding:'14px 16px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <span style={{color:C.accent,fontSize:12,fontWeight:700,whiteSpace:'nowrap',cursor:'pointer'}} onClick={()=>openDetail(order)}>View →</span>
                          <button onClick={e=>{e.stopPropagation();setDeleteOrderId(order.id);setDeleteOrderNumber(order.order_number || order.id);}}
                            style={{padding:'4px 10px',borderRadius:8,border:'1px solid #fecaca',background:'#fff5f5',color:'#ef4444',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected&&selectedFull&&(
          <OrderModal order={selectedFull} onClose={()=>{setSelected(null);setSelectedFull(null);}} onUpdate={fetchOrders} gst={gst} deliveryCharge={deliveryCharge} slug={slug}/>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteOrderId && (
          <div style={{position:'fixed',inset:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              style={{position:'absolute',inset:0,background:'rgba(26,15,0,0.55)',backdropFilter:'blur(4px)'}}
              onClick={()=>setDeleteOrderId(null)}/>
            <motion.div initial={{opacity:0,scale:0.9,y:20}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.9}}
              style={{position:'relative',background:'#fff',borderRadius:20,padding:32,width:'100%',maxWidth:340,textAlign:'center',boxShadow:'0 24px 64px rgba(0,0,0,0.2)',border:'1px solid #f0e4d8'}}>
              <div style={{width:64,height:64,borderRadius:18,background:'#fee2e2',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,margin:'0 auto 16px'}}>🗑️</div>
              <h3 style={{fontFamily:'"Playfair Display",serif',fontSize:18,fontWeight:700,color:'#1a0f00',margin:'0 0 8px'}}>Delete Order #{deleteOrderNumber}?</h3>
              <p style={{color:'#a07858',fontSize:13,margin:'0 0 24px'}}>This action cannot be undone.</p>
              <div style={{display:'flex',gap:12}}>
                <button onClick={()=>setDeleteOrderId(null)} disabled={deleting}
                  style={{flex:1,padding:'11px 0',borderRadius:11,border:'1.5px solid #f0e4d8',background:'#fff',color:'#6b4c35',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
                  Cancel
                </button>
                <button onClick={handleDeleteOrder} disabled={deleting}
                  style={{flex:1,padding:'11px 0',borderRadius:11,border:'none',background:'linear-gradient(135deg,#ef4444,#dc2626)',color:'#fff',fontSize:14,fontWeight:700,cursor:deleting?'not-allowed':'pointer',fontFamily:'inherit',boxShadow:'0 4px 14px rgba(239,68,68,0.35)'}}>
                  {deleting?'Deleting...':'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.55}}`}</style>
    </div>
  );
};

export default AdminOrders;
